import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchServices,
  createService,
  updateService,
  deleteService,
} from '../store/slices/questsSlice';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ServiceFormDialog from '../components/services/ServiceFormDialog';

const ServicesPage = () => {
  const dispatch = useDispatch();
  const { services, servicesLoading } = useSelector((state) => state.quests);

  const [formOpen, setFormOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    dispatch(fetchServices({ force: true }));
  }, [dispatch]);

  const showNotification = (message, severity = 'info') =>
    setNotification({ open: true, message, severity });

  const handleCreate = () => {
    setEditingService(null);
    setFormOpen(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormOpen(true);
  };

  const handleDelete = (service) => {
    if (!window.confirm(`Удалить вид заявки "${service.name}"?`)) return;
    dispatch(deleteService(service.id))
      .unwrap()
      .then(() => showNotification('Вид заявки удалён', 'success'))
      .catch((err) => showNotification(
        typeof err === 'string' && err ? err : 'Ошибка удаления',
        'error'
      ));
  };

  const handleSave = async (data) => {
    try {
      if (editingService) {
        await dispatch(updateService({ serviceId: editingService.id, serviceData: data })).unwrap();
        showNotification('Вид заявки обновлён', 'success');
      } else {
        await dispatch(createService(data)).unwrap();
        showNotification('Вид заявки создан', 'success');
      }
      setFormOpen(false);
      setEditingService(null);
    } catch (err) {
      showNotification(err || 'Ошибка сохранения', 'error');
    }
  };

  const formatPrice = (price) => {
    if (price == null) return '—';
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(price);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 132px)', display: 'flex', flexDirection: 'column', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 18 }}>В</Typography>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Виды заявок
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Управление видами заявок ({services?.length || 0})
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Новый вид
        </Button>
      </Box>

      {/* Table */}
      {servicesLoading && !services?.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', flex: 1 }}>
          <TableContainer sx={{ height: '100%' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa' }}>Название</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa' }}>Описание</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa', width: 160 }} align="right">
                    Стоимость
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa', width: 100 }} align="center">
                    Действия
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">Нет видов заявок</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  services?.map((service) => (
                    <TableRow
                      key={service.id}
                      hover
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {service.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {service.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatPrice(service.price)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Редактировать">
                          <IconButton size="small" onClick={() => handleEdit(service)} sx={{ color: 'primary.main' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton size="small" onClick={() => handleDelete(service)} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Form dialog */}
      <ServiceFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingService(null); }}
        onSave={handleSave}
        service={editingService}
      />

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.severity === 'error' ? 10000 : 4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: 120 }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServicesPage;
